import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, XCircle, CheckCircle2, Info, ShieldCheck, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { useClientPerms } from "../lib/useClientPerms";
import { toast } from "sonner";

const STATUS = {
  CLEAR: { label: "Clear", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", Icon: CheckCircle2 },
  REVIEW: { label: "Review", cls: "bg-amber-100 text-amber-700 border-amber-300", Icon: AlertTriangle },
  BLOCKED: { label: "Blocked", cls: "bg-red-100 text-red-700 border-red-300", Icon: XCircle },
};
const SEV = {
  BLOCKER: { dot: "bg-red-500", text: "text-red-800", Icon: XCircle },
  WARNING: { dot: "bg-amber-500", text: "text-amber-800", Icon: AlertTriangle },
  INFO: { dot: "bg-neutral-400", text: "text-neutral-600", Icon: Info },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.CLEAR;
  const Icon = s.Icon;
  return (
    <span data-testid="exceptions-status" className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" /> {s.label}
    </span>
  );
}

function ExceptionRow({ ex, runId, snapshot, onChanged }) {
  const sev = SEV[ex.severity] || SEV.INFO;
  const [busy, setBusy] = useState(false);
  const { canEdit } = useClientPerms();
  const canAck = canEdit("run_payroll") && !snapshot && ex.severity === "WARNING" && !ex.acknowledged;
  const ack = async () => {
    const reason = ex.acknowledgement_required ? window.prompt("Reason for acknowledging this warning:") : "acknowledged";
    if (ex.acknowledgement_required && !reason) return;
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${runId}/exceptions/acknowledge`,
        { code: ex.code, employee_id: ex.employee_id || null, reason: reason || "" });
      toast.success("Acknowledged");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not acknowledge");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`exception-${ex.code}${ex.employee_id ? "-" + ex.employee_id : ""}`}
      className="flex items-start gap-2.5 py-2.5 border-b border-border last:border-0">
      <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${sev.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${sev.text}`}>{ex.title}</span>
          {ex.acknowledged && <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 border border-emerald-300 px-1.5 py-0.5">Acknowledged</span>}
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">{ex.message}</p>
      </div>
      {canAck && (
        <button data-testid={`ack-${ex.code}`} disabled={busy} onClick={ack}
          className="shrink-0 text-xs border border-amber-400 text-amber-700 hover:bg-amber-500 hover:text-white px-2.5 py-1 transition-colors disabled:opacity-50">
          Acknowledge
        </button>
      )}
    </div>
  );
}

export default function PayrollExceptionsPanel({ runId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const load = useCallback(() => {
    api.get(`/payroll/runs/${runId}/exceptions`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [runId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const { status, blocker_count = 0, warning_count = 0, info_count = 0, exceptions = [], sections = [], snapshot } = data;
  const bySection = {};
  exceptions.forEach((e) => { (bySection[e.section] = bySection[e.section] || []).push(e); });

  return (
    <div data-testid="payroll-exceptions-panel" className="border border-border bg-white mb-8">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-neutral-50 transition-colors">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Payroll Exceptions & Compliance Review</span>
        <StatusPill status={status} />
        <span className="text-[11px] font-mono tabular-nums text-muted-foreground ml-auto flex items-center gap-3">
          <span className="text-red-600">{blocker_count} blocker{blocker_count === 1 ? "" : "s"}</span>
          <span className="text-amber-600">{warning_count} warning{warning_count === 1 ? "" : "s"}</span>
          <span>{info_count} info</span>
          {snapshot && <span className="text-neutral-400 uppercase tracking-widest">frozen</span>}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          {status === "CLEAR" && exceptions.filter((e) => e.severity !== "INFO").length === 0 ? (
            <p className="text-sm text-emerald-700 py-2">No blockers or warnings — this payroll looks clean.</p>
          ) : null}
          {sections.map((sec) => (
            <div key={sec.key} className="mt-4 first:mt-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{sec.label}</span>
                <span className="text-[10px] font-mono text-muted-foreground">
                  {sec.blocker_count ? `${sec.blocker_count}B ` : ""}{sec.warning_count ? `${sec.warning_count}W ` : ""}{sec.info_count ? `${sec.info_count}I` : ""}
                </span>
              </div>
              {(bySection[sec.key] || []).map((ex, i) => (
                <ExceptionRow key={ex.code + (ex.employee_id || "") + i} ex={ex} runId={runId} snapshot={snapshot} onChanged={load} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
